





























































import { Vue, Component } from "vue-property-decorator";
import { PreviewOpenTimeTrackingAutoCloseResponse } from "@/data/models/TimeTracking";
import { getTaskLabel } from "@/data/repository/TimeTrackingRepository";
import { DEFAULT_TIMEZONE } from "@/utils/TimezoneHelper";
import { formatTimestampAsHHMM, parseHHMMToMinutes } from "@/utils/DateHelper";

/** Result returned by the dialog's show() promise. */
export interface StaleCloseDialogResult {
  accepted: boolean;
  /** Present only when the user changed the proposed end time in the input. */
  userEditedEndAtMs?: number;
}

// ── Private helpers ──

/**
 * Reconstructs a Date from the stale month doc ID and day key.
 * monthDocId: "YYYY-M{zeroIndexedMonth}" e.g. "2026-M5" = June 2026.
 * dayKey: "MM-DD" with 1-indexed month.
 * Kept here as it is specific to the time tracking month doc ID format.
 */
function parseStaleDayToDate(monthDocId: string, dayKey: string): Date | null {
  const monthMatch = monthDocId.match(/^(\d{4})-M(\d+)$/);
  if (!monthMatch) return null;
  const year = parseInt(monthMatch[1], 10);
  const zeroIndexedMonth = parseInt(monthMatch[2], 10);
  const parts = dayKey.split("-");
  if (parts.length !== 2) return null;
  const day = parseInt(parts[1], 10);
  return new Date(year, zeroIndexedMonth, day);
}

/**
 * Computes the user-edited end timestamp in ms.
 * Uses a delta approach: applies the minute-level difference between the edited
 * and proposed HH:MM values to the original proposedEndAtMs, avoiding timezone
 * reconstruction complexity.
 *
 * Returns null when the edited time string is invalid.
 */
function computeEditedEndMs(
  proposedEndAtMs: number,
  proposedHHMM: string,
  editedHHMM: string
): number | null {
  const proposed = parseHHMMToMinutes(proposedHHMM);
  const edited = parseHHMMToMinutes(editedHHMM);
  if (proposed == null || edited == null) return null;
  const deltaMs = (edited - proposed) * 60000;
  return proposedEndAtMs + deltaMs;
}

@Component
export default class StaleCloseConfirmationDialogComponent extends Vue {
  isShowing = false;

  private preview: PreviewOpenTimeTrackingAutoCloseResponse | null = null;
  private clientTimezone: string = DEFAULT_TIMEZONE;

  /** Proposed end time formatted as "HH:MM" in the client timezone (used to detect changes). */
  private proposedTimeString = "";

  editedTimeInput = "";
  timeInputError: string[] = [];

  private resolve?: (value: StaleCloseDialogResult) => void;

  // ── Computed display values ──

  get dayDisplay(): string {
    if (!this.preview?.staleMonthDocId || !this.preview?.staleDayKey) return "";
    const date = parseStaleDayToDate(this.preview.staleMonthDocId, this.preview.staleDayKey);
    if (!date) return `${this.preview.staleDayKey}`;
    return date.toLocaleDateString(this.$i18n.locale, {
      weekday: "long",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  /** Project + shared entries carry project/task info; "other" entries do not. */
  get isProjectEntry(): boolean {
    return this.preview?.entryType === "project" || this.preview?.entryType === "shared";
  }

  /** Office project number if set, otherwise the IRES number; falls back to the project name. */
  get projectNumberDisplay(): string {
    if (!this.preview) return "";
    const office = this.preview.projectNumberOffice;
    const number = office && office !== "" ? office : this.preview.projectNumberIres ?? "";
    return number && number !== "" ? number : this.preview.projectName ?? "";
  }

  /** Translated label of the open task, or "" when none. */
  get taskDisplay(): string {
    const key = getTaskLabel(this.preview?.task ?? null);
    return key ? this.$t(key).toString() : "";
  }

  get proposedEndDisplay(): string {
    const proposedMs = this.preview?.proposal?.proposedEndAtMs;
    if (proposedMs == null) return "—";
    return formatTimestampAsHHMM(proposedMs, this.clientTimezone);
  }

  // ── Public API ──

  /**
   * Shows the stale-close confirmation dialog.
   * Returns the user's decision and optional edited end time.
   */
  show(
    preview: PreviewOpenTimeTrackingAutoCloseResponse,
    clientTimezone: string
  ): Promise<StaleCloseDialogResult> {
    this.preview = preview;
    this.clientTimezone = clientTimezone || DEFAULT_TIMEZONE;
    this.timeInputError = [];

    const proposedMs = preview.proposal?.proposedEndAtMs;
    if (proposedMs != null) {
      this.proposedTimeString = formatTimestampAsHHMM(proposedMs, this.clientTimezone);
    } else {
      this.proposedTimeString = "";
    }
    this.editedTimeInput = this.proposedTimeString;

    this.isShowing = true;

    return new Promise<StaleCloseDialogResult>((resolve) => {
      this.resolve = resolve;
    });
  }

  // ── Event handlers ──

  onAccept() {
    const proposedMs = this.preview?.proposal?.proposedEndAtMs;

    if (proposedMs == null) {
      this.isShowing = false;
      this.resolve?.({ accepted: true });
      return;
    }

    // Determine whether the user actually edited the time
    const inputChanged = this.editedTimeInput && this.editedTimeInput !== this.proposedTimeString;

    if (inputChanged) {
      const userEditedEndAtMs = computeEditedEndMs(
        proposedMs,
        this.proposedTimeString,
        this.editedTimeInput
      );

      if (userEditedEndAtMs == null) {
        this.timeInputError = [this.$t("staleEntryAutoClose.invalidTime").toString()];
        return;
      }

      this.isShowing = false;
      this.resolve?.({ accepted: true, userEditedEndAtMs });
    } else {
      this.isShowing = false;
      this.resolve?.({ accepted: true });
    }
  }
}
